import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {
    GridApiPro,
    GridColDef,
    GridPaginationModel, GridRowParams,
    useGridApiRef
} from '@mui/x-data-grid-pro';
import {useLazyQuery} from "@apollo/client";
import _ from "lodash";
import {paginationLimit} from "../../config/paginationLimit";
import Table from "../Table";
import moment from "moment/moment";
import {InstalledBaseEventList} from "../../graphql/queries/installedBaseEventList";
import {InstalledBaseEventListFilter, InstalledBaseEventListSort, SearchInput} from "../../codegen/graphql";
import {Typography} from "@mui/joy";
import {Box} from '@mui/material';
import {useTranslation} from "react-i18next";
import { TFunction } from 'i18next';
import {useUserResources, getInitialPagination} from "@dreebit-registry/vsm-microservice-components";
import formatEventEnum from "../../utils/formatEventEnum";
import {useTableColumnConfig} from "../../hooks/useTableColumnConfig";
import {TableColumnStorageConfig} from "../../types";
import {getExtraEventColumns, EXTRA_EVENT_COLUMN_FIELDS, EXTRA_EVENT_DEFAULT_VISIBILITY} from "./eventExtraColumns";
import ContaminationDeclarationStatusChip from "../ContaminationDeclarationStatus/StatusChip";
import EventRowActions from "./EventRowActions";
import {isContaminationDeclarationsEnabled} from "../../utils/contaminationDeclarationsEnabled";

export const INSTALLED_BASE_EVENT_UPDATE_RESOURCE = "installedBaseLifecycleEventUpdate";

/** Previous/next event of the open detail panel, across page boundaries. */
export interface EventNavigation {
    hasPrev: boolean
    hasNext: boolean
    goPrev: () => void
    goNext: () => void
}



interface Props {
    height: number| string,
    onChangePagination?: (newPaginationModel: GridPaginationModel) => void,
    urlParams?: URLSearchParams,
    filter?: InstalledBaseEventListFilter
    search?: SearchInput
    sort?: InstalledBaseEventListSort
    onOpenEventDetailView: (id: string | number, options?: { replace?: boolean }) => void
    onEditEvent?: (id: string | number) => void
    selectedEventId: string | number | null
    onNavigationChange?: (navigation: EventNavigation | null) => void
    /** Text of the empty table; the grid's default otherwise. */
    noRowsLabel?: string
    isGlobalEventList?: boolean
    apiRef?: React.MutableRefObject<GridApiPro>
    panelAnchorRef?: React.RefObject<HTMLElement | null>
    t?: TFunction
}



const getDefaultColumns = (
    t: TFunction,
    isGlobalEventList?: boolean,
    onEditEvent?: (id: string | number) => void,
    showDeclarationColumn?: boolean,
):  GridColDef[] => {


    const extraColumns = getExtraEventColumns(t).map((col): GridColDef => ({
        field: col.field,
        headerName: col.headerName,
        flex: 1,
        disableColumnMenu: true,
        sortable: false,
        valueGetter: (_value, row) => col.getValue(row),
    }));
    const eventIdColumn = extraColumns.find((col) => col.field === 'eventId');

    return [
        // Event ID is the first column (hidden by default) so it leads the export.
        ...(isGlobalEventList && eventIdColumn ? [eventIdColumn] : []),
        {
            field: 'eventType',
            headerName: t('Event'),
            flex: 1,
            display: "flex",
            disableColumnMenu: true,
            valueGetter: (value) => formatEventEnum(value, t),
        },
        {
            field: 'insert',
            headerName: t('Date'),
            flex: 1,
            disableColumnMenu: true,
            renderCell: (params) => {
                const insertDate = _.get(params.row, 'insert')

                return  moment(insertDate).format('L LT')
            },
        },
        {
            field: 'user',
            headerName: t('Author'),
            flex: 1,
            disableColumnMenu: true,
            valueGetter: (value, row) => {
                return _.get(row, 'user.name')
            },
        },

        {
            field: 'eventCategory',
            headerName: t('Category'),
            flex: 1,
            disableColumnMenu: true,
            valueGetter: (value) => formatEventEnum(value, t),
        },

        ...(isGlobalEventList ? [
            {
                field: 'asset',
                headerName: t('Asset'),
                flex: 3,
                disableColumnMenu: true,
                renderCell: (params: any) => {
                    const asset = params.row.asset;
                    if (!asset) return null;
                    const name = asset.product?.name;
                    const serial = asset.serialNumber;
                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', height: '100%' }}>
                            {name && <span>{name}</span>}
                            {serial && (
                                <Typography level="body" sx={{ fontStyle: 'italic' }}>
                                    (S/N: {serial})
                                </Typography>
                            )}
                        </Box>
                    );
                },
            },
            {
                field: 'message',
                headerName: t('Message'),
                flex: 3,
                disableColumnMenu: true,
            }
        ] : []),

        // Additional Semicon export columns — hidden by default, toggled via the
        // columns panel and included in the CSV export. (eventId leads the list.)
        ...(isGlobalEventList ? extraColumns.filter((col) => col.field !== 'eventId') : []),

        // Missing-/present-DoC indicator on ASSET_REMOVE rows (per-asset history only;
        // the chip renders nothing for other event types).
        ...(showDeclarationColumn ? [
            {
                field: 'contaminationDeclaration',
                headerName: t('DoC'),
                description: t('Declaration of Contamination'),
                width: 150,
                display: "flex",
                sortable: false,
                disableColumnMenu: true,
                renderCell: (params: any) => (
                    <Box onClick={(e) => e.stopPropagation()}>
                        <ContaminationDeclarationStatusChip eventId={String(params.id)} eventType={params.row?.eventType}/>
                    </Box>
                ),
            } as GridColDef,
        ] : []),

        // Row actions: "Edit" (lifecycle events, when allowed) and the DoC
        // create/open action on ASSET_REMOVE rows (mirrors the DoC column's chip).
        ...((onEditEvent || showDeclarationColumn) ? [
            {
                field: 'actions',
                headerName: '',
                width: 60,
                sortable: false,
                resizable: false,
                disableColumnMenu: true,
                hideable: false,
                disableReorder: true,
                renderCell: (params: any) => (
                    <EventRowActions
                        eventId={String(params.id)}
                        eventType={params.row?.eventType}
                        eventCategory={params.row?.eventCategory}
                        onEditEvent={onEditEvent}
                        withDeclaration={showDeclarationColumn}
                    />
                ),
            } as GridColDef,
        ] : [])
    ]
}






// The row actions stay last and are not offered in the columns panel (no header label)
const FIXED_COLUMN_FIELDS = ['actions'];

// Configs saved before the column order was persisted hold MUI's raw model, in which a
// missing key means "visible" ("Show all" deletes keys). From now on a missing key means
// "default", which hides the extra columns — so spell out the old meaning once.
const migrateLegacyColumnConfig = (saved: TableColumnStorageConfig): TableColumnStorageConfig => {
    if (saved.columnOrder?.length) return saved;
    const visibility = saved.columnVisibilityModel ?? {};
    return {
        ...saved,
        columnVisibilityModel: {
            ...visibility,
            ...Object.fromEntries(EXTRA_EVENT_COLUMN_FIELDS.map((field) => [field, visibility[field] !== false])),
        },
    };
};

const EventListTable = ({height, onChangePagination, urlParams, filter, onOpenEventDetailView, onEditEvent, selectedEventId, onNavigationChange, noRowsLabel, isGlobalEventList, apiRef, panelAnchorRef, search}: Props) => {

    const {t} = useTranslation();
    const resources = useUserResources();
    const canEdit = resources.includes(INSTALLED_BASE_EVENT_UPDATE_RESOURCE);
    const editHandler = canEdit && onEditEvent ? onEditEvent : undefined;
    const showDeclarationColumn = !isGlobalEventList && isContaminationDeclarationsEnabled();
    const columns = useMemo(
        () => getDefaultColumns(t, isGlobalEventList, editHandler, showDeclarationColumn),
        [t, isGlobalEventList, editHandler, showDeclarationColumn]
    );

    // Column management is only offered in the global event list
    const columnConfig = useTableColumnConfig({
        storageKey: 'eventList_columnConfig',
        columns,
        defaultVisibility: EXTRA_EVENT_DEFAULT_VISIBILITY,
        fixedFields: FIXED_COLUMN_FIELDS,
        enabled: !!isGlobalEventList,
        migrate: migrateLegacyColumnConfig,
    });

    const [paginationModel, setPaginationModel] = useState(getInitialPagination(paginationLimit, urlParams));

    useEffect(() => {
        if (onChangePagination) {
            onChangePagination(paginationModel)
        }
    }, [paginationModel])

    const [fetchEventList, eventListQuery] = useLazyQuery(InstalledBaseEventList, {
        context: {
            clientName: "federatedGraphEndpoint"
        },
        fetchPolicy: "network-only"
    });
    const eventListData = _.get(eventListQuery, 'data.installedBaseEventList.installedBaseEvents', []);
    const totalEvents = _.get(eventListQuery, 'data.installedBaseEventList.total')

    useEffect(() => {
        fetchEventList({
            variables: {
                filter: filter ?? {},
                search: search ?? { query: '', fields: [] },
                pagination: {
                    start: paginationModel.page * paginationModel.pageSize,
                    limit: paginationModel.pageSize,
                },
                sort: { insert: "desc" },
            },
        });
    }, [paginationModel, filter, search]); // eslint-disable-line react-hooks/exhaustive-deps

    const internalApiRef = useGridApiRef();
    const gridApiRef = apiRef ?? internalApiRef;

    // Paging through events from the detail panel. At a page boundary the table turns
    // the page and opens the first (or last) event once that page has loaded.
    const rowIds: string[] = eventListData.map((event: any) => String(event.id));
    const selectedIndex = selectedEventId != null ? rowIds.indexOf(String(selectedEventId)) : -1;
    const pageCount = totalEvents ? Math.ceil(totalEvents / paginationModel.pageSize) : 0;
    const [pendingSelection, setPendingSelection] = useState<{ page: number, edge: 'first' | 'last' } | null>(null);
    const navigable = selectedIndex >= 0 && !eventListQuery.loading && !pendingSelection;
    const hasPrev = navigable && (selectedIndex > 0 || paginationModel.page > 0);
    const hasNext = navigable && (selectedIndex < rowIds.length - 1 || paginationModel.page < pageCount - 1);

    const goPrev = () => {
        if (!hasPrev) return;
        if (selectedIndex > 0) {
            onOpenEventDetailView(rowIds[selectedIndex - 1], {replace: true});
        } else {
            setPendingSelection({page: paginationModel.page - 1, edge: 'last'});
            setPaginationModel({...paginationModel, page: paginationModel.page - 1});
        }
    };

    const goNext = () => {
        if (!hasNext) return;
        if (selectedIndex < rowIds.length - 1) {
            onOpenEventDetailView(rowIds[selectedIndex + 1], {replace: true});
        } else {
            setPendingSelection({page: paginationModel.page + 1, edge: 'first'});
            setPaginationModel({...paginationModel, page: paginationModel.page + 1});
        }
    };

    // Wait until the rows of the target page are loaded: right after the page change the
    // previous page's rows are still in place for one render.
    const loadedStart = eventListQuery.variables?.pagination?.start;
    useEffect(() => {
        if (!pendingSelection || eventListQuery.loading) return;
        if (loadedStart !== pendingSelection.page * paginationModel.pageSize) return;
        setPendingSelection(null);
        if (rowIds.length === 0) return;
        onOpenEventDetailView(pendingSelection.edge === 'first' ? rowIds[0] : rowIds[rowIds.length - 1], {replace: true});
    }, [pendingSelection, eventListQuery.loading, loadedStart, eventListData]); // eslint-disable-line react-hooks/exhaustive-deps

    const navigationRef = useRef({goPrev, goNext});
    navigationRef.current = {goPrev, goNext};
    const stableGoPrev = useCallback(() => navigationRef.current.goPrev(), []);
    const stableGoNext = useCallback(() => navigationRef.current.goNext(), []);

    useEffect(() => {
        onNavigationChange?.({hasPrev, hasNext, goPrev: stableGoPrev, goNext: stableGoNext});
    }, [hasPrev, hasNext]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => () => onNavigationChange?.(null), []); // eslint-disable-line react-hooks/exhaustive-deps

    // Keep the open event visible while paging through the list. Deferred, because after a
    // page change the grid takes over the new rows only after this render.
    useEffect(() => {
        if (selectedIndex < 0) return;
        const timer = setTimeout(() => gridApiRef.current?.scrollToIndexes?.({rowIndex: selectedIndex}), 0);
        return () => clearTimeout(timer);
    }, [selectedIndex, eventListData]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Box
            sx={{
                width: "100%",
                height: height,
                    '& .MuiDataGrid-row': {
                    cursor: 'pointer',
                    },
                '& .MuiDataGrid-row.Mui-selected': {
                    backgroundColor: '#FFEEE6 !important',
                },
            }}
        >
            <Table
                loading={eventListQuery.loading}
                data={eventListData}
                rowCount={totalEvents}
                {...columnConfig.gridProps}
                pagination={true}
                paginationMode="server"
                paginationModel={paginationModel}
                pageSizeOptions={[paginationLimit]}
                onPaginationModelChange={setPaginationModel}
                apiRef={gridApiRef}
                {...(noRowsLabel && {localeText: {noRowsLabel}})}
                {...(isGlobalEventList && {panelAnchorRef})}
                disableColumnFilter
                disableColumnSorting
                disableRowSelectionOnClick
                disableColumnResize
                disableColumnReorder={!isGlobalEventList}
                disableColumnPinning
                hideFooterSelectedRowCount
                rowSelectionModel={selectedEventId ? [selectedEventId] : []}
                onRowClick={(row: GridRowParams) => {
                    onOpenEventDetailView(row.id);
                }}

            />

        </Box>
    );
}

export default EventListTable;
